import { useState } from "react";
import { Numerical, Question } from "../../types";
import {
  MultiChoice,
  SingleChoice,
  Reorder,
  DragAndDrop,
  SingleTypeIn,
  MultiTypeIn,
} from "@quark/questions";
import { Answer } from "@quark/shared-types";
import AnswerLocker from "../answer-locker";
import { Loading, Notification } from "@quark/design";
import { unlockQuestion } from "../../api";
import styles from "./index.module.scss";
import { useChatGpt } from "@quark/ai-assistant";

export interface QuestionRendererProps {
  className?: string;
  id: Numerical;
  title: string;
  question: Question;
  onNext?: () => void;
  onPrev?: () => void;
  courseNo: string;
  onSubmitAnswer?: (id: Numerical, data: Answer[]) => void;
}

const QuestionRenderer = ({
  id,
  question,
  title,
  onPrev,
  onNext,
  onSubmitAnswer,
  courseNo,
}: QuestionRendererProps) => {
  const [loading, setLoading] = useState(false);
  const handleSubmitAnswer = (userAnswer: Answer[]) => {
    onSubmitAnswer?.(id, userAnswer);
  };

  const chatGpt = useChatGpt();

  const handleFillAnswer = async () => {
    try {
      setLoading(true);
      await unlockQuestion(id);
      const quizAnswer = question.quizAnswer;
      onSubmitAnswer?.(id, quizAnswer);
    } catch (message: any) {
      Notification.error(message as string);
    } finally {
      setLoading(false);
    }
  };

  const handleSendQuizPrompt = () => {
    chatGpt.sendQuizPrompt({
      courseNo,
      questionId: Number(id),
      mode: question.userAnswer?.length ? "help" : "hint",
    });
  };

  const renderUnLocker = () => {
    return (
      <AnswerLocker
        isRight={question.isRight}
        onFillAnswer={handleFillAnswer}
      />
    );
  };

  const renderQuestion = (question: Question) => {
    if (question.type === "drag_and_drop") {
      return (
        <DragAndDrop
          title={title}
          content={question.text}
          options={question.options}
          userAnswer={question.userAnswer}
          quizAnswer={question.quizAnswer}
          isRight={question.isRight}
          onPrev={onPrev}
          onNext={onNext}
          onSubmit={handleSubmitAnswer}
          unLocker={renderUnLocker()}
        />
      );
    }

    if (question.type === "single_choice") {
      return (
        <SingleChoice
          title={title}
          content={question.text}
          options={question.options}
          userAnswer={question.userAnswer}
          quizAnswer={question.quizAnswer}
          isRight={question.isRight}
          onPrev={onPrev}
          onNext={onNext}
          onSubmit={handleSubmitAnswer}
          unLocker={renderUnLocker()}
        />
      );
    }

    if (question.type === "mult_choice") {
      return (
        <MultiChoice
          title={title}
          content={question.text}
          options={question.options}
          userAnswer={question.userAnswer}
          quizAnswer={question.quizAnswer}
          isRight={question.isRight}
          onPrev={onPrev}
          onNext={onNext}
          onSubmit={handleSubmitAnswer}
          unLocker={renderUnLocker()}
        />
      );
    }

    if (question.type === "reorder") {
      return (
        <Reorder
          options={question.options}
          title={title}
          content={question.text}
          userAnswer={question.userAnswer}
          quizAnswer={question.quizAnswer}
          isRight={question.isRight}
          onPrev={onPrev}
          onNext={onNext}
          onSubmit={handleSubmitAnswer}
          unLocker={renderUnLocker()}
        />
      );
    }

    if (question.type === "type_in") {
      return (
        <SingleTypeIn
          title={title}
          content={question.text}
          options={question.options}
          userAnswer={question.userAnswer}
          quizAnswer={question.quizAnswer}
          isRight={question.isRight}
          onPrev={onPrev}
          onNext={onNext}
          onSubmit={handleSubmitAnswer}
          unLocker={renderUnLocker()}
        />
      );
    }

    if (question.type === "multi_type_in") {
      return (
        <MultiTypeIn
          title={title}
          content={question.text}
          options={question.options}
          userAnswer={question.userAnswer}
          quizAnswer={question.quizAnswer}
          isRight={question.isRight}
          onPrev={onPrev}
          onNext={onNext}
          onSubmit={handleSubmitAnswer}
          unLocker={renderUnLocker()}
        />
      );
    }

    return null;
  };

  return (
    <div className={styles.questionRenderer}>
      <div className={styles.chatGptWrapper}>
        <button className={styles.sendButton} onClick={handleSendQuizPrompt}>
          AI 答疑
        </button>
      </div>
      {renderQuestion(question)}
      {loading && <Loading fullscreen />}
    </div>
  );
};

export default QuestionRenderer;
